import type { ActionsBlock } from '../spec/types'

/**
 * The set of things an operator may do to the subject on screen — and,
 * deliberately, the things they may not.
 *
 * Verbatim from `docs/design/reference/blocks/actions.html`. Each action is a
 * label, a sentence of consequence, a tag naming the role or law that governs
 * it, and one control.
 *
 * The block's real work is the refusals. "Freeze account → No such control
 * exists", tagged `Law 1`, ships as a dimmed tile rather than being dropped
 * from the grid: an absent control reads as an oversight and gets escalated to
 * engineering, whereas a present-but-refused one states that frozen is an
 * EMI-imposed state we display with FinXP's attribution and never set (law 1,
 * CLAUDE.md invariants 1 and 8). The corpus's one exception is break-glass,
 * where the spec supplies a tile whose own copy says the control is hidden
 * until a session is active.
 *
 * `writeBlocked` dims every tile on top of each action's own flag. It is true
 * on the standby cell and for a role that lacks the screen's permission — a
 * write against an async replica either errors or, after a bad promotion,
 * double-processes (CLAUDE.md Part I §3), so the whole surface goes cold at
 * once rather than tile by tile.
 *
 * The note line reserves `min-height:2.5em` so tiles of unequal copy length do
 * not leave their buttons on ragged baselines; the consequence sentence is the
 * part that must be read before the click, and a shuffling row invites
 * skipping it.
 */

interface Props {
  readonly block: ActionsBlock
  readonly writeBlocked: boolean
}

export function Actions({ block, writeBlocked }: Props) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1px',
        background: 'var(--k-line)',
        border: '1px solid var(--k-line)',
      }}
    >
      {block.actions.map(([label, note, cta, tag, tone, disabled]) => (
        <div
          key={label}
          style={{
            flex: 1,
            minWidth: '210px',
            background: 'var(--k-panel)',
            padding: '9px 11px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>{label}</span>
            <span
              data-tone={tone}
              style={{
                marginLeft: 'auto',
                padding: '0 5px',
                fontSize: '9px',
                fontWeight: 800,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
              }}
            >
              {tag}
            </span>
          </div>
          <div
            style={{
              fontSize: '11px',
              color: 'var(--k-mut)',
              marginTop: '4px',
              minHeight: '2.5em',
              textWrap: 'pretty',
            }}
          >
            {note}
          </div>
          <button
            type="button"
            className="k-hov"
            data-dim={disabled || writeBlocked ? '1' : ''}
            style={{
              height: '26px',
              padding: '0 10px',
              marginTop: '5px',
              border: '1px solid var(--k-line)',
              background: 'var(--k-panel)',
              color: 'var(--k-ink)',
              font: 'inherit',
              fontSize: '11.5px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {cta}
          </button>
        </div>
      ))}
    </div>
  )
}
